/**
 * =============================================================================
 * SPEQ55 RFP Pro - Reset Password Page
 * =============================================================================
 *
 * This component handles the password reset completion flow. Users arrive
 * here by clicking the reset link in their email. It handles:
 *
 * - Extracting the reset token from URL query params
 * - New password input with confirmation
 * - Password strength validation
 * - Form submission to complete the reset
 * - Success/error message display
 * - Redirect to login after success
 *
 * URL FORMAT:
 * -----------
 * /reset-password?token=<reset-token-from-email>
 *
 * The token is a JWT that contains:
 * - User ID
 * - Expiration time (24 hours from creation)
 * - Single-use flag (invalidated after first use)
 *
 * PASSWORD REQUIREMENTS:
 * ----------------------
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 *
 * SECURITY CONSIDERATIONS:
 * ------------------------
 * - Token validation happens on the server
 * - Expired/used tokens are rejected
 * - All existing sessions are revoked after password change
 * - Password strength requirements enforced
 *
 * @module pages/auth/ResetPassword
 * @author SPEQ55
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/client';
import speq55Logo from '../../assets/speq55-logo.png';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Password requirements for display.
 * These match the server-side validation rules.
 */
const PASSWORD_REQUIREMENTS = [
  {
    id: 'length',
    label: 'At least 8 characters',
    check: (pw) => pw.length >= 8,
  },
  {
    id: 'uppercase',
    label: 'One uppercase letter',
    check: (pw) => /[A-Z]/.test(pw),
  },
  {
    id: 'lowercase',
    label: 'One lowercase letter',
    check: (pw) => /[a-z]/.test(pw),
  },
  { id: 'number', label: 'One number', check: (pw) => /\d/.test(pw) },
];

// =============================================================================
// RESET PASSWORD PAGE COMPONENT
// =============================================================================

/**
 * Reset Password page component.
 *
 * Allows users to set a new password using the token from their email.
 * Validates password strength and confirmation before submission.
 *
 * @returns {JSX.Element} Reset password page
 */
function ResetPassword() {
  // ===========================================================================
  // HOOKS
  // ===========================================================================

  // Get URL search params for token
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Extract token from URL
  const token = searchParams.get('token');

  // ===========================================================================
  // STATE
  // ===========================================================================

  /**
   * Form input values.
   */
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  /**
   * Form validation errors.
   */
  const [errors, setErrors] = useState({});

  /**
   * General error message.
   */
  const [generalError, setGeneralError] = useState('');

  /**
   * Loading state during form submission.
   */
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Whether the password has been successfully reset.
   */
  const [isSuccess, setIsSuccess] = useState(false);

  /**
   * Whether the page is in an invalid state (no token).
   */
  const [isInvalidToken, setIsInvalidToken] = useState(false);

  // ===========================================================================
  // EFFECTS
  // ===========================================================================

  /**
   * Check for token on mount.
   * If no token is present, show error state.
   */
  useEffect(() => {
    if (!token) {
      setIsInvalidToken(true);
    }
  }, [token]);

  // ===========================================================================
  // FORM HANDLERS
  // ===========================================================================

  /**
   * Handles input field changes.
   *
   * @param {Object} event - Input change event
   */
  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field-specific errors
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // Clear general error
    if (generalError) {
      setGeneralError('');
    }
  };

  /**
   * Validates the form before submission.
   *
   * @returns {boolean} True if form is valid
   */
  const validateForm = () => {
    const newErrors = {};

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      // Check all password requirements
      const failedRequirements = PASSWORD_REQUIREMENTS.filter(
        (req) => !req.check(formData.password)
      );

      if (failedRequirements.length > 0) {
        newErrors.password = 'Password does not meet all requirements';
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission.
   * Sends reset request to server and handles response.
   *
   * @param {Object} event - Form submit event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Clear previous errors
    setGeneralError('');

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Send reset request
      const response = await api.post('/auth/reset-password', {
        token,
        password: formData.password,
      });

      if (response.data?.success) {
        setIsSuccess(true);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Password reset successful. Please login with your new password.',
            },
          });
        }, 3000);
      } else {
        setGeneralError(response.data?.error || 'Failed to reset password');
      }
    } catch (error) {
      // Handle specific error types
      const errorMessage = error.response?.data?.error;

      if (errorMessage?.includes('expired')) {
        setGeneralError('This password reset link has expired. Please request a new one.');
      } else if (errorMessage?.includes('Invalid')) {
        setGeneralError('This password reset link is invalid. Please request a new one.');
      } else {
        setGeneralError(errorMessage || 'Failed to reset password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Gets the status of a password requirement.
   *
   * @param {Object} requirement - Requirement object
   * @returns {string} 'met', 'unmet', or 'neutral'
   */
  const getRequirementStatus = (requirement) => {
    if (!formData.password) return 'neutral';
    return requirement.check(formData.password) ? 'met' : 'unmet';
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================

  // Invalid token state
  if (isInvalidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img src={speq55Logo} alt="SPEQ55" className="h-16 w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-blue-600">RFP Pro</h1>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">Invalid Reset Link</h2>
          </div>

          <div className="bg-white py-8 px-6 shadow-lg rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Missing Reset Token</h3>
              <p className="mt-2 text-sm text-gray-600">
                This password reset link appears to be invalid or incomplete. Please request a new
                password reset.
              </p>
            </div>

            <div className="mt-6">
              <Link
                to="/forgot-password"
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Request New Reset Link
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img src={speq55Logo} alt="SPEQ55" className="h-16 w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-blue-600">RFP Pro</h1>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">Password Reset Complete</h2>
          </div>

          <div className="bg-white py-8 px-6 shadow-lg rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Success!</h3>
              <p className="mt-2 text-sm text-gray-600">
                Your password has been reset successfully. You will be redirected to the login page
                in a moment.
              </p>
            </div>

            <div className="mt-6">
              <Link
                to="/login"
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header Section */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <img src={speq55Logo} alt="SPEQ55" className="h-16 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-blue-600">RFP Pro</h1>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Create new password</h2>
          <p className="mt-2 text-sm text-gray-600">Enter your new password below</p>
        </div>

        {/* Form Card */}
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg sm:px-10">
          {/* General Error Alert */}
          {generalError && (
            <div
              className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded"
              role="alert"
              aria-live="polite"
            >
              <div className="flex">
                <svg
                  className="h-5 w-5 text-red-500 mr-2 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-red-700">{generalError}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            {/* New Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`
                    appearance-none block w-full px-3 py-2 border rounded-md shadow-sm
                    placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500
                    sm:text-sm
                    ${errors.password ? 'border-red-500 text-red-900' : 'border-gray-300'}
                    ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}
                  `}
                  placeholder="Enter new password"
                  aria-invalid={errors.password ? 'true' : 'false'}
                  aria-describedby={errors.password ? 'password-error' : 'password-requirements'}
                />
              </div>

              {/* Password Requirements Checklist */}
              <div id="password-requirements" className="mt-3 space-y-1">
                <p className="text-xs text-gray-500 font-medium">Password must have:</p>
                <ul className="space-y-1">
                  {PASSWORD_REQUIREMENTS.map((req) => {
                    const status = getRequirementStatus(req);
                    return (
                      <li
                        key={req.id}
                        className={`text-xs flex items-center ${
                          status === 'met'
                            ? 'text-green-600'
                            : status === 'unmet'
                              ? 'text-red-600'
                              : 'text-gray-500'
                        }`}
                      >
                        {status === 'met' ? (
                          <svg
                            className="h-4 w-4 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : status === 'unmet' ? (
                          <svg
                            className="h-4 w-4 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        ) : (
                          <span className="h-4 w-4 mr-1 flex items-center justify-center">•</span>
                        )}
                        {req.label}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {errors.password && (
                <p id="password-error" className="mt-2 text-sm text-red-600" role="alert">
                  {errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`
                    appearance-none block w-full px-3 py-2 border rounded-md shadow-sm
                    placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500
                    sm:text-sm
                    ${errors.confirmPassword ? 'border-red-500 text-red-900' : 'border-gray-300'}
                    ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}
                  `}
                  placeholder="Confirm new password"
                  aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                  aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                />
              </div>
              {errors.confirmPassword && (
                <p id="confirmPassword-error" className="mt-2 text-sm text-red-600" role="alert">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`
                  w-full flex justify-center py-2.5 px-4 border border-transparent
                  rounded-md shadow-sm text-sm font-medium text-white
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                  ${
                    isSubmitting
                      ? 'bg-blue-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }
                `}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Resetting password...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </div>

            {/* Back to login link */}
            <div className="text-center">
              <Link to="/login" className="text-sm font-medium text-blue-600 hover:text-blue-500">
                ← Back to login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default ResetPassword;
